import { useCallback } from 'react'

import type { InfiniteData } from '@tanstack/react-query'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import {
  useAccessToken,
  useDecodedAccessToken,
} from '@b-stock/fe-core/bstock-next/components'
import { getHost } from '@b-stock/fe-core/bstock-next/helpers'
import { getNotificationReadReceiptsClient } from '@b-stock/fe-core/bstock-next/queries'
import { buildAxiosRequestConfig } from '@b-stock/fe-core/bstock-react'
import type {
  PaginatedNotificationReadReceiptDto,
  UpdateNotificationReadReceiptDto,
} from '@b-stock/notification-api-client'

import { getNotificationListKey } from './notificationListQuery'
import {
  getNotificationUnreadCountKey,
  type NotificationUnreadCountData,
} from './notificationUnreadCountQuery'

type NotificationListData = InfiniteData<PaginatedNotificationReadReceiptDto>

const withReceiptsMarkedRead = (
  data: NotificationListData | undefined,
  isTarget: (receiptId: string) => boolean
): NotificationListData | undefined => {
  if (!data) return data
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      receipts: page.receipts.map((receipt) =>
        isTarget(receipt._id) ? { ...receipt, read: true } : receipt
      ),
    })),
  }
}

const wasReceiptUnread = (
  data: NotificationListData | undefined,
  id: string
): boolean =>
  data?.pages
    .flatMap((page) => page.receipts)
    .some((receipt) => receipt._id === id && !receipt.read) ?? false

// Preserves pusherChannelName - only count changes here. Leaving the cache
// entry untouched when it's not loaded yet (rather than inventing a count)
// also means useNotificationRealtimeUpdates never sees a spurious channel
// name appear/disappear as a side effect of this mutation.
const decrementUnreadCount = (
  data: NotificationUnreadCountData | undefined
): NotificationUnreadCountData | undefined =>
  data ? { ...data, count: Math.max(0, data.count - 1) } : data

const zeroUnreadCount = (
  data: NotificationUnreadCountData | undefined
): NotificationUnreadCountData | undefined =>
  data ? { ...data, count: 0 } : data

/**
 * Marks every notification read for the current person. Optimistically flips
 * every cached item rather than only the ones currently rendered in the
 * tray, since the server call (`markAllRead`) is unscoped too - a buyer with
 * more history loaded than fits on screen shouldn't see stale unread dots
 * reappear after the next fetch. The badge count is zeroed outright for the
 * same reason - every notification is read after this call, not just the
 * ones in the cached list page(s).
 */
export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient()
  const accessToken = useAccessToken()
  const decodedToken = useDecodedAccessToken()
  const personId = decodedToken?.personId ?? ''
  const listKey = getNotificationListKey(personId)
  const unreadCountKey = getNotificationUnreadCountKey(personId)

  return useMutation({
    mutationFn: () =>
      getNotificationReadReceiptsClient().readReceiptsApiControllerMarkAllRead(
        { personId },
        buildAxiosRequestConfig({ accessToken: accessToken ?? '' })
      ),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: listKey })
      await queryClient.cancelQueries({ queryKey: unreadCountKey })
      const previousListData =
        queryClient.getQueryData<NotificationListData>(listKey)
      const previousUnreadCount =
        queryClient.getQueryData<NotificationUnreadCountData>(unreadCountKey)

      queryClient.setQueryData<NotificationListData>(listKey, (old) =>
        withReceiptsMarkedRead(old, () => true)
      )
      queryClient.setQueryData<NotificationUnreadCountData>(
        unreadCountKey,
        zeroUnreadCount
      )

      return { previousListData, previousUnreadCount }
    },
    onError: (_error, _vars, context) => {
      if (!context) return
      queryClient.setQueryData(listKey, context.previousListData)
      queryClient.setQueryData(unreadCountKey, context.previousUnreadCount)
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: listKey })
      void queryClient.invalidateQueries({ queryKey: unreadCountKey })
    },
  })
}

/**
 * Marks a single notification read for the tray's click-through path.
 *
 * fe-core's NotificationRowLink is a bare `styled.a` (fe-core has no
 * dependency on Next.js, so it can't render a Next `<Link>`), so clicking a
 * tray item with a destination triggers a real browser navigation/unload,
 * not client-side routing. Two things make the generated API client unsafe
 * for that:
 *   1. Its param creator does `await setBearerAuthToObject(...)` before
 *      axios is ever called (see readReceiptsApiControllerUpdateReadReceipt
 *      in @b-stock/notification-api-client) - a real asynchronous gap, even
 *      with no awaits of our own, that the browser's navigation can land in
 *      before the request is dispatched at all.
 *   2. Even dispatched, axios's default XHR adapter has no `keepalive`
 *      support (that's fetch-only) - nothing stops the in-flight request
 *      from being aborted by the navigation/unload itself.
 * This hook bypasses the generated client for this one call and fires a raw
 * `fetch` with `keepalive: true` - the actual spec-sanctioned mechanism for
 * "this request must survive navigation" - built from the same base path,
 * auth header, and body shape the generated client would have produced.
 */
export const useMarkNotificationReadOnNavigate = () => {
  const queryClient = useQueryClient()
  const accessToken = useAccessToken()
  const decodedToken = useDecodedAccessToken()
  const personId = decodedToken?.personId ?? ''

  return useCallback(
    (id: string) => {
      const listKey = getNotificationListKey(personId)
      const unreadCountKey = getNotificationUnreadCountKey(personId)
      const previousListData =
        queryClient.getQueryData<NotificationListData>(listKey)
      const previousUnreadCount =
        queryClient.getQueryData<NotificationUnreadCountData>(unreadCountKey)
      // Re-check against the cache rather than trusting the caller's
      // (possibly stale, memoized) `item.read` - a realtime update or
      // another tab may have already marked this receipt read.
      const wasUnread = wasReceiptUnread(previousListData, id)

      queryClient.setQueryData<NotificationListData>(listKey, (old) =>
        withReceiptsMarkedRead(old, (receiptId) => receiptId === id)
      )
      if (wasUnread) {
        queryClient.setQueryData<NotificationUnreadCountData>(
          unreadCountKey,
          decrementUnreadCount
        )
      }

      // `getNotificationReadReceiptsClient()` wraps this same lookup
      // ('NOTIFICATIONS' is the hostName fe-core builds that client with)
      // but its `basePath` field is `protected` in the generated client's
      // types - `getHost` is the public, type-safe way to get the same
      // value without bypassing that with a cast.
      const basePath = getHost('NOTIFICATIONS')
      const body: UpdateNotificationReadReceiptDto = { read: true }

      // Duplicates the route the generated client builds for
      // readReceiptsApiControllerUpdateReadReceipt - update this if that
      // route ever changes, since this call bypasses the client entirely.
      const fetchPromise = fetch(
        `${basePath}/v1/notification-read-receipts/${encodeURIComponent(id)}`,
        {
          method: 'PATCH',
          keepalive: true,
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify(body),
        }
      )

      // Fired after the request is already on the wire (not awaited, so it
      // can't delay dispatch): without this, a refetch that was already
      // in-flight before the click can resolve after our optimistic update
      // above and briefly restore stale read:false/the old badge count.
      void queryClient.cancelQueries({ queryKey: listKey })
      void queryClient.cancelQueries({ queryKey: unreadCountKey })

      fetchPromise
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `Failed to mark notification read: ${response.status}`
            )
          }
        })
        .catch(() => {
          queryClient.setQueryData(listKey, previousListData)
          queryClient.setQueryData(unreadCountKey, previousUnreadCount)
        })
        .finally(() => {
          void queryClient.invalidateQueries({ queryKey: listKey })
          void queryClient.invalidateQueries({ queryKey: unreadCountKey })
        })
    },
    [queryClient, accessToken, personId]
  )
}
